import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { debounceTime, distinctUntilChanged } from 'rxjs';

import { DemandStatus } from '../../../../../../Enums/demand-status.enum';
import { Platform } from '../../../../../../Enums/platform.enum';
import { COMMON_IMPORTS } from '../../../../../Shared/common-imports';
import { SEARCH_DEBOUNCE_MS } from '../../../../../Catalog/Consts/search-debounce.const';
import { DemandsDateRangeChange } from '../../../../../Catalog/Interfaces/demands-filter-panel.interface';
import { PLATFORM_SWITCH_OPTIONS } from '../../../../../Catalog/Consts/platform-switch.const';
import {
  ALL_DEMANDS_DEVICE_MAX_LENGTH,
  ALL_DEMANDS_STATUS_VALUES,
} from '../../../Consts/list-all-demands.const';
import { AllDemandsFilterCriteria } from '../../../Interfaces/all-demands-filters.interface';
import { ReportPickerOption } from '../../../Interfaces/report-picker-option.interface';
import { ReportPickerState } from '../../../Types/report-picker-state.type';
import { ReportSubjectTab } from '../../../Types/report-subject-tab.type';
import { AllDemandsDateRangeComponent } from '../all-demands-date-range/all-demands-date-range.component';
import { AllDemandsPickerComponent } from '../all-demands-picker/all-demands-picker.component';

@Component({
  selector: 'app-all-demands-filter-panel',
  standalone: true,
  imports: [COMMON_IMPORTS, AllDemandsDateRangeComponent, AllDemandsPickerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './all-demands-filter-panel.component.html',
  styleUrl: './all-demands-filter-panel.component.scss',
})
export class AllDemandsFilterPanelComponent {
  readonly tab = input.required<ReportSubjectTab>();
  readonly criteria = input.required<AllDemandsFilterCriteria>();
  readonly activeFilterCount = input.required<number>();
  readonly hasFilters = input.required<boolean>();
  readonly optionsState = input<ReportPickerState>('idle');
  readonly requesterOptions = input<ReadonlyArray<ReportPickerOption>>([]);
  readonly subjectOptions = input<ReadonlyArray<ReportPickerOption>>([]);
  readonly approverOptions = input<ReadonlyArray<ReportPickerOption>>([]);
  readonly moderatorOptions = input<ReadonlyArray<ReportPickerOption>>([]);

  readonly criteriaChange = output<Partial<AllDemandsFilterCriteria>>();
  readonly clearAll = output<void>();
  readonly opened = output<void>();

  readonly panelOpen = signal(false);

  readonly statusValues: ReadonlyArray<DemandStatus> = ALL_DEMANDS_STATUS_VALUES;
  readonly platformOptions = PLATFORM_SWITCH_OPTIONS;
  readonly deviceMaxLength = ALL_DEMANDS_DEVICE_MAX_LENGTH;

  readonly deviceControl = new FormControl('', { nonNullable: true });

  readonly inertOnGroups = computed(() => this.tab() === 'group');

  readonly overlayPositions: ConnectedPosition[] = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 8 },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -8 },
  ];

  constructor() {
    this._syncDeviceControlFromCriteria();
    this._bindDeviceInput();
  }

  private _syncDeviceControlFromCriteria(): void {
    effect(() => {
      const device = this.criteria().device;
      if (this.deviceControl.value.trim() !== device) {
        this.deviceControl.setValue(device, { emitEvent: false });
      }
    });
  }

  private _bindDeviceInput(): void {
    this.deviceControl.valueChanges
      .pipe(debounceTime(SEARCH_DEBOUNCE_MS), distinctUntilChanged(), takeUntilDestroyed())
      .subscribe((value) => this._emitDevice(value.trim()));
  }

  private _emitDevice(device: string): void {
    if (device !== this.criteria().device) this.criteriaChange.emit({ device });
  }

  togglePanel(): void {
    const opening = !this.panelOpen();
    this.panelOpen.set(opening);
    if (opening) this.opened.emit();
  }

  closePanel(): void {
    this.panelOpen.set(false);
  }

  onOverlayKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') this.closePanel();
  }

  isStatusActive(status: DemandStatus): boolean {
    return this.criteria().statuses.includes(status);
  }

  onToggleStatus(status: DemandStatus): void {
    const current = this.criteria().statuses;
    const next = current.includes(status)
      ? current.filter((value) => value !== status)
      : this.statusValues.filter((value) => value === status || current.includes(value));
    this.criteriaChange.emit({ statuses: next });
  }

  onClearStatuses(): void {
    this.criteriaChange.emit({ statuses: [] });
  }

  onPlatformChange(platform: Platform | null): void {
    this.criteriaChange.emit({ platform });
  }

  onRequestedRangeChange(range: DemandsDateRangeChange): void {
    this.criteriaChange.emit({ requestedFrom: range.from, requestedTo: range.to });
  }

  onUpdatedRangeChange(range: DemandsDateRangeChange): void {
    this.criteriaChange.emit({ updatedFrom: range.from, updatedTo: range.to });
  }

  onRequesterChange(requesterId: string | null): void {
    this.criteriaChange.emit({ requesterId });
  }

  onSubjectChange(subjectId: string | null): void {
    this.criteriaChange.emit({ subjectId });
  }

  onApproverChange(approverId: string | null): void {
    this.criteriaChange.emit({ approverId });
  }

  onModeratorChange(moderatorId: string | null): void {
    this.criteriaChange.emit({ moderatorId });
  }

  onClearAll(): void {
    this.clearAll.emit();
  }
}
